import DiscoverabilityAccordion from '@components/common/DiscoverabilityAccordion'
import { EditorGrid, EditorSectionHeading } from '@components/common/EditorWorkspace'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import MenuItem from '@mui/material/MenuItem'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd'
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import InfoOutlineRoundedIcon from '@mui/icons-material/InfoOutlined'
import { GlassCard, GlassButton, GlassOutlineButton } from '@components/common/Glass'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import EditOffRoundedIcon from '@mui/icons-material/EditOffRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'
import {
  helperTextColor,
  shadow1,
  mainColorDarker,
  mainColor,
  shadow2,
  mainColorLighter,
  mainColorDarkText,
  mainColorMediumText,
  altColor,
  altColorDarkText,
  altColorLighter,
} from 'src/ui/theme/tokens'
import { useEffect, useMemo, useRef, useState } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import AddIcon from '@mui/icons-material/Add'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Item } from '@components/common/ItemGuessBox'
import { createTopic } from 'src/api/openai'
import { createList, getList, getListCategories, getListTags } from 'src/api/lists'
import { err, range, split } from 'src/utils/functions'
import { sortIndex, toSoloItems } from 'src/pages/dashboard/listUtils'
import { AlertInput } from '@components/common/Alert'
import { List, ListItem } from 'src/api/types/lists'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Chip from '@mui/material/Chip'
import PlayButton from '@components/common/PlayButton'
import { AnimatePresence, motion } from 'framer-motion'

type CreateListSectionProps = {
  fullScreen: boolean
  multiMode: string
  gameCode?: string
  hostId?: string
  connectionId?: string
  mode: 'single' | 'multi' | 'host' | undefined
  userId?: number
  onStartGame: (payload: { topic: string; items: Item[]; listId?: number }) => void
  initialDraft?: {
    prompt: string
    items: string[]
    categories?: string[]
    tags?: string[]
    visibility?: 'public' | 'private'
  }
  onConvertToPoll?: (payload: {
    prompt: string
    items: string[]
    categories: string[]
    tags: string[]
    visibility: 'public' | 'private'
  }) => void
  onEditList?: (list: List) => void
  onAlert?: (value: AlertInput) => void
  sounds?: { click?: () => void; button?: () => void; swipe?: () => void }
}

const ManualSortableItem = ({
  item,
  index,
  onTitleChange,
  onMetricChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  disableUp,
  disableDown,
  disabled,
}: {
  item: Item
  index: number
  onTitleChange: (value: string) => void
  onMetricChange: (value: string) => void
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  disableUp: boolean
  disableDown: boolean
  disabled?: boolean
}) => {
  return (
    <GlassCard
      sx={{
        px: 1,
        py: 0,
        borderRadius: '22px',
        borderColor: 'rgba(220,220,220,0.75)',
        boxShadow: shadow2,
        background: '#ffffff',
      }}
    >
      <Box display="flex" justifyContent="center" alignItems="center" gap={0.33}>
        {/* <Box
     
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            borderRadius: '10px',
            background: 'rgba(148,163,184,0.15)',
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box> */}
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap={0}
          pr={1}
        >
          <IconButton size="small" onClick={onMoveUp} disabled={disableUp}>
            <KeyboardArrowUpRoundedIcon fontSize="small" />
          </IconButton>
          <Box
            sx={{
              minWidth: 28,
              height: 28,
              borderRadius: '999px',
              backgroundColor: '#e2e8f0',
              color: '#162f49',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13,
              position: 'relative',
            }}
          >
            {index + 1}
          </Box>
          <IconButton size="small" onClick={onMoveDown} disabled={disableDown}>
            <KeyboardArrowDownRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          display="flex"
          flexGrow={1}
          justifyContent="center"
          alignItems="center"
          height="100%"
          flexDirection="column"
          pt={1}
        >
          <TextField
            disabled={disabled}
            fullWidth
            size="small"
            placeholder="Item title"
            value={item.title}
            onChange={(e) => onTitleChange(e.target.value)}
            InputProps={{
              sx: {
                background: '#ffffff',
                borderRadius: '12px',
                '& fieldset': {
                  borderColor: '#d6e4ef',
                },
              },
            }}
          />
          <TextField
            disabled={disabled}
            fullWidth
            variant="standard"
            size="small"
            placeholder="metric (optional)"
            value={item.metric || ''}
            onChange={(e) => onMetricChange(e.target.value)}
            sx={{ mt: 0.5, opacity: 0.7 }}
            InputProps={{
              disableUnderline: true,
              style: {
                borderRadius: '12px',
                fontSize: '14px',
              },
            }}
          />
        </Box>
      </Box>
    </GlassCard>
  )
}

export default function CreateListSection({
  fullScreen,
  multiMode,
  gameCode,
  hostId,
  connectionId,
  mode,
  userId,
  onStartGame,
  initialDraft,
  onConvertToPoll,
  onEditList,
  onAlert,
  sounds,
}: CreateListSectionProps): JSX.Element {
  const [localManualCreate, setLocalManualCreate] = useState(false)
  const [manualModeModalOpen, setManualModeModalOpen] = useState(false)
  const [topic, setTopic] = useState('')
  const [length, setLength] = useState<number>(10)
  const [listItems, setListItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedList, setSelectedList] = useState<number | undefined>(undefined)
  const [saving, setSaving] = useState(false)
  const [createMethod, setCreateMethod] = useState<'prompt' | 'manual'>('manual')
  const [savedList, setSavedList] = useState<List | undefined>(undefined)
  const [itemsAnimKey, setItemsAnimKey] = useState(0)
  const [suggestPoll, setSuggestPoll] = useState(false)
  const [pollSuggestionDismissed, setPollSuggestionDismissed] = useState(false)
  const isBusy = loading || saving
  const [visibility, setVisibility] = useState<'public' | 'private'>('public')
  const [forcedPrivateByModeration, setForcedPrivateByModeration] = useState(false)
  const [listCategoryOptions, setListCategoryOptions] = useState<string[]>([])
  const [listTagOptions, setListTagOptions] = useState<string[]>([])
  const [listCategories, setListCategories] = useState<string[]>([])
  const [listTags, setListTags] = useState<string[]>([])
  const listContainerRef = useRef<HTMLDivElement | null>(null)
  const manualCreate = localManualCreate
  const manualMode = manualCreate || mode === 'host'
  const isLocked = !!selectedList
  const validTitleCount = listItems.filter((item) => item.title?.trim()).length
  const hasValidItemCount = listItems.length === 10
  const hasAllTitles = validTitleCount === 10 && listItems.length === 10
  const manualItems = useMemo(
    () => listItems.slice().sort((a, b) => (a.index || 0) - (b.index || 0)),
    [listItems]
  )
  const mergedListCategoryOptions = useMemo(
    () => Array.from(new Set([...(listCategoryOptions || []), ...listCategories])).filter(Boolean),
    [listCategoryOptions, listCategories]
  )
  const mergedListTagOptions = useMemo(
    () => Array.from(new Set([...(listTagOptions || []), ...listTags])).filter(Boolean),
    [listTagOptions, listTags]
  )
  const normalizeMetaValue = (value: string) => value.trim().slice(0, 16)
  const normalizeMetaList = (value: string[]) =>
    value.map(normalizeMetaValue).filter(Boolean).slice(0, 3)
  const filterOption = useMemo(() => createFilterOptions<string>(), [])

  const normalizeItems = (items: Item[]) =>
    items.map((item, i) => ({
      ...item,
      index: i + 1,
      points: 1,
    }))

  const shuffleItems = (items: Item[]) => {
    const shuffled = items.slice()
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1))
      const shuffleI = shuffled[i]
      const shuffleJ = shuffled[j]
      if (!shuffleI || !shuffleJ) continue
      shuffled[i] = shuffleJ
      shuffled[j] = shuffleI
    }
    return shuffled
  }

  const formatGenerateError = (message?: string) => {
    if (!message) return 'Failed to generate list. Please try again.'
    let raw = message
    try {
      const parsed = JSON.parse(message)
      raw = parsed?.error?.message || parsed?.message || message
      if (parsed?.error?.status === 'UNAVAILABLE' || parsed?.error?.code === 503) {
        return 'AI is currently busy. Please try again in a moment.'
      }
    } catch {
      // keep raw
    }
    if (/high demand|try again later|unavailable/i.test(raw)) {
      return 'AI is currently busy. Please try again in a moment.'
    }
    const retryMatch = message.match(/retry in ([0-9.]+)s/i)
    const retryDelayMatch = message.match(/retryDelay\\":\\"([0-9]+)s/i)
    const seconds = retryMatch?.[1] || retryDelayMatch?.[1]
    if (/quota|RESOURCE_EXHAUSTED/i.test(raw)) {
      return `You hit the free-tier limit. Please try again${seconds ? ` in ${Math.round(Number(seconds))}s` : ''}.`
    }
    return raw
  }

  const handleCreateList = () => {
    if (selectedList) return
    if (!topic || !listItems.length) return
    if (!hasValidItemCount) {
      onAlert?.({
        open: true,
        severity: 'warning',
        message: 'Lists must have exactly 10 items.',
      })
      return
    }
    if (!hasAllTitles) {
      onAlert?.({
        open: true,
        severity: 'warning',
        message: 'All list items must have a title.',
      })
      return
    }
    setSaving(true)
    const listItemsType: ListItem[] = listItems.map((l) => ({
      listId: 0,
      title: l.title,
      points: 1,
      metric: l.metric,
      rank: l.index,
      index: l.index,
    }))
    createList({
      list: {
        prompt: topic,
        items: listItemsType,
        createMethod,
        private: visibility === 'private',
        category: listCategories.join(','),
        tags: listTags.join(','),
      },
    })
      .then((res) => {
        setSaving(false)
        if (res.success && res.data) {
          setSelectedList(res.data.id)
          setSavedList(res.data)
          return
        }
        let message = res.message || 'Error creating list.'
        try {
          const parsed = JSON.parse(message)
          message = parsed?.error?.message || parsed?.message || message
        } catch {
          // keep original
        }
        onAlert?.({
          open: true,
          severity: 'error',
          message,
        })
      })
      .catch((e) => {
        setSaving(false)
        let message = e?.response?.data?.message || e?.message || 'Error creating list.'
        try {
          const parsed = JSON.parse(message)
          message = parsed?.error?.message || parsed?.message || message
        } catch {
          // keep original
        }
        onAlert?.({
          open: true,
          severity: 'error',
          message,
        })
        err(e)
      })
  }

  const applyGeneratedItems = (items: Item[]) => {
    const trimmedItems = items.slice(0, 10)
    if (mode === 'host') {
      setListItems(normalizeItems(sortIndex(trimmedItems.slice())))
      setItemsAnimKey((prev) => prev + 1)
      sounds?.swipe?.()
      return
    }
    const normalized =
      mode === 'single'
        ? toSoloItems(trimmedItems.slice(), 10)
        : normalizeItems(trimmedItems.slice())
    setListItems(shuffleItems(normalized))
    setItemsAnimKey((prev) => prev + 1)
    sounds?.swipe?.()
  }

  useEffect(() => {
    if (!itemsAnimKey) return
    if (!sounds?.swipe) return
    if (manualMode) return
    if (!listItems.length) return
    const timers = listItems.map((_, idx) => window.setTimeout(() => sounds.swipe?.(), idx * 120))
    return () => {
      timers.forEach((id) => window.clearTimeout(id))
    }
  }, [itemsAnimKey, listItems.length, manualMode, sounds])

  const handleAutoPrompt = () => {
    if (isBusy) return
    if (multiMode === 'party' && gameCode && hostId !== connectionId) {
      return
    }
    setSelectedList(undefined)
    setLoading(true)
    setSuggestPoll(false)
    setPollSuggestionDismissed(false)
    setTopic('')
    setListItems([])
    const quantity = mode === 'single' ? 10 : length ? length : undefined
    createTopic('', quantity, 'trivia')
      .then((res) => {
        setLoading(false)
        if (!res.success) {
          const message = formatGenerateError(res?.message)
          onAlert?.({ open: true, severity: 'warning', message })
          return
        }
        if (res.success && res.data) {
          const moderationFlag =
            res.data.restricted ||
            res.data.moderationStatus === 'offensive' ||
            res.data.moderationStatus === 'blocked'
          setForcedPrivateByModeration(!!moderationFlag)
          if (moderationFlag) setVisibility('private')
          setCreateMethod('prompt')
          setSuggestPoll(Boolean(res.data.suggestPoll))
          const nextTopic = res.data.prompt || ''
          setListCategories(res.data.category ? split(res.data.category, ',').slice(0, 3) : [])
          setListTags(res.data.tags ? split(res.data.tags, ',').slice(0, 3) : [])
          const items =
            res.data.items?.map((item, i) => ({
              title: item.title,
              metric: item.metric,
              index: Number(item.index || item.rank || i + 1),
              points: 1,
              listId: res.data?.id || 0,
            })) || []
          setTopic(nextTopic)
          applyGeneratedItems(items)
        }
      })
      .catch((e) => {
        setLoading(false)
        onAlert?.({
          open: true,
          severity: 'error',
          message: 'Failed to generate list. Please try again.',
        })
        err(e)
      })
  }

  const handleCreateTopic = () => {
    if (isBusy) return
    if (!topic) {
      handleAutoPrompt()
      return
    }
    setSelectedList(undefined)
    setLoading(true)
    setSuggestPoll(false)
    setPollSuggestionDismissed(false)
    setListItems([])
    const quantity = mode === 'single' ? 10 : length ? length : undefined
    createTopic(topic, quantity, 'trivia')
      .then((res) => {
        setLoading(false)
        if (!res.success) {
          const message = formatGenerateError(res?.message)
          onAlert?.({ open: true, severity: 'warning', message })
          return
        }
        if (res.success && res.data) {
          const moderationFlag =
            res.data.restricted ||
            res.data.moderationStatus === 'offensive' ||
            res.data.moderationStatus === 'blocked'
          setForcedPrivateByModeration(!!moderationFlag)
          if (moderationFlag) setVisibility('private')
          setCreateMethod('prompt')
          setSuggestPoll(Boolean(res.data.suggestPoll))
          const nextTopic = res.data.prompt || topic
          setListCategories(res.data.category ? split(res.data.category, ',').slice(0, 3) : [])
          setListTags(res.data.tags ? split(res.data.tags, ',').slice(0, 3) : [])
          const items =
            res.data.items?.map((item, i) => ({
              title: item.title,
              metric: item.metric,
              index: Number(item.index || item.rank || i + 1),
              points: 1,
              listId: res.data?.id || 0,
            })) || []
          setTopic(nextTopic)
          applyGeneratedItems(items)
        }
      })
      .catch((e) => {
        setLoading(false)
        onAlert?.({
          open: true,
          severity: 'error',
          message: 'Failed to generate list. Please try again.',
        })
        err(e)
      })
  }

  const handleItemChange = (itemIndex: number, value: string, itemPosition?: number) => {
    setListItems((prev) => {
      const normalizedIndex = Number(itemIndex)
      let didUpdate = false
      const byIndex = prev.map((item) => {
        if (Number(item.index) === normalizedIndex) {
          didUpdate = true
          return { ...item, title: value }
        }
        return item
      })
      if (didUpdate) return byIndex
      if (typeof itemPosition !== 'number') return prev
      const sorted = prev.slice().sort((a, b) => Number(a.index || 0) - Number(b.index || 0))
      const target = sorted[itemPosition]
      if (!target) return prev
      return prev.map((item) => (item === target ? { ...item, title: value } : item))
    })
  }

  const handleItemMetricChange = (itemIndex: number, value: string, itemPosition?: number) => {
    setListItems((prev) => {
      const normalizedIndex = Number(itemIndex)
      let didUpdate = false
      const byIndex = prev.map((item) => {
        if (Number(item.index) === normalizedIndex) {
          didUpdate = true
          return { ...item, metric: value }
        }
        return item
      })
      if (didUpdate) return byIndex
      if (typeof itemPosition !== 'number') return prev
      const sorted = prev.slice().sort((a, b) => Number(a.index || 0) - Number(b.index || 0))
      const target = sorted[itemPosition]
      if (!target) return prev
      return prev.map((item) => (item === target ? { ...item, metric: value } : item))
    })
  }

  const handleStartGame = () => {
    if (!topic || !listItems.length) return
    if (!hasValidItemCount) {
      onAlert?.({
        open: true,
        severity: 'warning',
        message: 'Lists must have exactly 10 items to play.',
      })
      return
    }
    if (!hasAllTitles) {
      onAlert?.({
        open: true,
        severity: 'warning',
        message: 'All list items must have a title to play.',
      })
      return
    }
    onStartGame({
      topic,
      items: listItems,
      listId: selectedList,
    })
  }

  const handleDeleteItem = (itemIndex: number) => {
    const next = listItems.filter((item) => item.index !== itemIndex)
    setListItems(normalizeItems(next))
  }

  const handleAddItem = () => {
    // no action
    if (listItems.length >= 10) return

    // fill with empty items
    if (!listItems.length) {
      setListItems(
        range(10).map((_, i) => ({
          title: '',
          index: i + 1,
          points: 1,
          editable: true,
        }))
      )
      return
    }
    // add just one ?
    setListItems((prev) =>
      normalizeItems(
        prev.concat([
          {
            title: '',
            index: prev.length + 1,
            points: 1,
            editable: true,
          },
        ])
      )
    )
  }
  const handleMoveItem = (from: number, to: number) => {
    if (to < 0 || to >= manualItems.length) return
    const next = arrayMove(manualItems, from, to)
    setListItems(normalizeItems(next))
  }

  const handleClear = () => {
    setSelectedList(undefined)
    setSavedList(undefined)
    setCreateMethod('manual')
    setForcedPrivateByModeration(false)
    setTopic('')
    setListItems([])
    setListCategories([])
    setListTags([])
    setLoading(false)
    setSaving(false)
    setSuggestPoll(false)
    setPollSuggestionDismissed(false)
    setLocalManualCreate(false)
    setManualModeModalOpen(false)
  }

  useEffect(() => {
    if (!listCategoryOptions.length) {
      getListCategories()
        .then((res) => {
          if (res.success && res.data?.length) {
            setListCategoryOptions(res.data)
          }
        })
        .catch(() => {
          // ignore
        })
    }
    if (!listTagOptions.length) {
      getListTags()
        .then((res) => {
          if (res.success && res.data?.length) {
            setListTagOptions(res.data)
          }
        })
        .catch(() => {
          // ignore
        })
    }
  }, [listCategoryOptions.length, listTagOptions.length])

  const handleViewList = () => {
    if (!selectedList || !onEditList) return
    if (savedList?.id === selectedList && savedList.items?.length) {
      onEditList(savedList)
      return
    }
    getList(selectedList)
      .then((res) => {
        if (res.success && res.data) {
          setSavedList(res.data)
          onEditList(res.data)
          return
        }
        onAlert?.({
          open: true,
          severity: 'warning',
          message: res.message || 'Could not load list.',
        })
      })
      .catch((e) => {
        err(e)
        onAlert?.({
          open: true,
          severity: 'error',
          message: 'Could not load list.',
        })
      })
  }

  useEffect(() => {
    if (!initialDraft) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalManualCreate(false)
    setManualModeModalOpen(false)
    setSelectedList(undefined)
    setSavedList(undefined)
    setTopic(initialDraft.prompt || '')
    const nextItems = (initialDraft.items || []).slice(0, 10).map((title, i) => ({
      title: title || '',
      metric: '',
      index: i + 1,
      points: 1,
    }))
    while (nextItems.length < 10) {
      nextItems.push({
        title: '',
        metric: '',
        index: nextItems.length + 1,
        points: 1,
      })
    }
    setListItems(nextItems)
    setListCategories((initialDraft.categories || []).slice(0, 3))
    setListTags((initialDraft.tags || []).slice(0, 3))
    setVisibility(initialDraft.visibility || 'public')
    setForcedPrivateByModeration(false)
    setSuggestPoll(false)
    setPollSuggestionDismissed(false)
    setItemsAnimKey((prev) => prev + 1)
  }, [initialDraft])

  return (
    <Box pb={2}>
      <Box py={1}>
        <EditorGrid className="editor-create">
          <Box className="editor-title">
            <Box>
              <EditorSectionHeading
                step="01"
                title="Title / prompt"
                description="Choose a topic. Generate a list or enter your own items."
              />
              <Box display="flex" gap={1} alignItems="center">
                <Box flexGrow={1}>
                  <TextField
                    variant="outlined"
                    disabled={isLocked || isBusy}
                    InputProps={{
                      sx: {
                        background: '#ffffff',
                        borderRadius: '14px',
                        boxShadow: shadow1,
                        '& fieldset': {
                          borderColor: '#d6e4ef',
                        },
                      },
                    }}
                    fullWidth={true}
                    placeholder="Auto-generate a trivia list"
                    onKeyDown={(key) => {
                      if (key.code === 'Enter') {
                        const inputEl = document.activeElement as HTMLInputElement
                        inputEl?.blur()
                        void handleCreateTopic()
                      }
                    }}
                    value={topic}
                    onChange={(e) => {
                      setCreateMethod('manual')
                      setTopic(e.target.value)
                    }}
                  />
                </Box>
                <GlassButton
                  variant="contained"
                  aria-label="Generate"
                  onClick={() => {
                    sounds?.click?.()
                    handleCreateTopic()
                  }}
                  disabled={
                    isLocked ||
                    isBusy ||
                    (multiMode === 'party' && !!gameCode && hostId !== connectionId) ||
                    !topic
                  }
                  size="large"
                  sx={{
                    height: '56px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg,#65e0f8,#53d9f5)',
                    boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
                  }}
                >
                  {loading === true ? (
                    <CircularProgress
                      size="30px"
                      sx={{ color: mainColorDarkText, opacity: 0.225 }}
                    />
                  ) : (
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <AutoAwesomeIcon fontSize="small" />
                      <Typography
                        fontWeight={800}
                        fontSize={13}
                        sx={{ display: { xs: 'none', sm: 'block' } }}
                      >
                        Generate
                      </Typography>
                    </Box>
                  )}
                </GlassButton>
              </Box>
            </Box>
          </Box>
          <Box className="editor-main">
            <GlassCard
              sx={{
                py: { xs: 2, sm: 2.5 },
                px: 0,
                background: '#ffffff',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                sx={{
                  px: { xs: 2, sm: 2.5 },
                }}
              >
                <Box>
                  <EditorSectionHeading step="02" title="Items" />
                  {!manualMode ? (
                    <Typography variant="caption" color={helperTextColor} textAlign="left">
                      Items displayed in random order
                    </Typography>
                  ) : null}
                </Box>
                <Typography variant="body2" color="#64748b">
                  {listItems?.length || 0} items
                </Typography>
              </Stack>
              <Stack
                spacing={1}
                sx={{
                  px: { xs: 1, sm: 2 },
                }}
              >
                {!listItems.length && !loading && !localManualCreate && (
                  <Box sx={{ py: 3, px: 1.5, textAlign: 'left' }}>
                    <Box aria-hidden sx={{ display: 'grid', gap: 0.75, maxWidth: 240, mb: 2 }}>
                      {[1, 2, 3].map((rank) => (
                        <Box key={rank} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Box
                            sx={{
                              display: 'grid',
                              placeItems: 'center',
                              width: 27,
                              height: 28,
                              borderRadius: '9px',
                              background: '#e1f5fb',
                              color: '#087fa2',
                              fontSize: 11,
                              fontWeight: 800,
                            }}
                          >
                            {rank}
                          </Box>
                          <Box
                            sx={{
                              height: 10,
                              width: `${100 - rank * 15}%`,
                              borderRadius: '8px',
                              background: rank === 2 ? '#ffefdd' : '#e8f0f6',
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                    <Typography fontWeight={800} textAlign="left">
                      Your next great list starts here.
                    </Typography>
                    <Typography variant="body2" color={helperTextColor} textAlign="left" mt={0.5}>
                      Generate 10 items from your topic, or write your own below.
                    </Typography>
                  </Box>
                )}
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress size={48} sx={{ color: mainColorDarker }} />
                  </Box>
                ) : null}

                <Box
                  display="flex"
                  gap={1}
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  width="100%"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={`list-items-${itemsAnimKey}`}
                      initial={{ opacity: 0, y: 10, width: '100%' }}
                      animate={{ opacity: 1, y: 0, width: '100%' }}
                      exit={{ opacity: 0, y: -6, width: '100%' }}
                      transition={{
                        duration: 0.2,
                        ease: 'easeOut',
                        width: '100%',
                      }}
                    >
                      {manualMode ? (
                        <Stack spacing={1} width="100%">
                          {manualItems.map((item, i) => (
                            <motion.div
                              key={`${item.index}-${i}`}
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                delay: i * 0.12,
                                duration: 0.28,
                                ease: 'easeOut',
                              }}
                            >
                              <ManualSortableItem
                                item={item}
                                index={i}
                                onTitleChange={(value) => handleItemChange(item.index, value, i)}
                                onMetricChange={(value) =>
                                  handleItemMetricChange(item.index, value, i)
                                }
                                onDelete={() => {
                                  sounds?.click?.()
                                  handleDeleteItem(item.index)
                                }}
                                onMoveUp={() => handleMoveItem(i, i - 1)}
                                onMoveDown={() => handleMoveItem(i, i + 1)}
                                disableUp={isLocked || isBusy || i === 0}
                                disableDown={isLocked || isBusy || i === manualItems.length - 1}
                                disabled={isLocked || isBusy}
                              />
                            </motion.div>
                          ))}
                        </Stack>
                      ) : (
                        <Stack spacing={1} width="100%">
                          {listItems.map((item, i) => (
                            <motion.div
                              key={item.title + i}
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                delay: i * 0.12,
                                duration: 0.28,
                                ease: 'easeOut',
                              }}
                            >
                              <GlassCard
                                sx={{
                                  px: 1.5,
                                  py: 1.5,
                                  borderRadius: '28px',
                                  borderColor: 'rgba(220,220,220,0.75)',
                                  boxShadow: shadow2,
                                  background: '#ffffff',
                                }}
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box flexGrow={1} justifyContent="center">
                                    <Box
                                      display="flex"
                                      gap={0.5}
                                      justifyContent="center"
                                      alignItems="center"
                                    >
                                      <Box flexGrow={1}>
                                        <Typography
                                          variant="body1"
                                          fontSize={'18px'}
                                          fontWeight={500}
                                          textAlign="center"
                                        >
                                          {item.title}
                                        </Typography>
                                      </Box>
                                      <Tooltip title="Search item" arrow>
                                        <IconButton
                                          size="small"
                                          disabled={isLocked || isBusy}
                                          onClick={() => {
                                            window.open(
                                              `https://www.google.com/search?q=${item.title.replaceAll(' ', '+')}`,
                                              '_blank'
                                            )
                                          }}
                                          sx={{
                                            opacity: 0.6,
                                          }}
                                        >
                                          <OpenInNewRoundedIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                </Box>
                              </GlassCard>
                            </motion.div>
                          ))}
                        </Stack>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </Box>
                <Box display="flex" gap={1} justifyContent="flex-start" alignItems="center">
                  {!isLocked && !localManualCreate ? (
                    <GlassOutlineButton
                      variant="text"
                      onClick={() => {
                        sounds?.click?.()
                        if (isLocked) return
                        if (isBusy) return
                        if (!manualMode) {
                          setManualModeModalOpen(true)
                          return
                        }

                        handleAddItem()
                      }}
                      disabled={isLocked || isBusy}
                      sx={{
                        width: 'fit-content',
                        fontWeight: 500,
                        color: mainColorDarker,
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        transition:
                          'transform 120ms ease-in, background-color 120ms ease-in, color 120ms ease-in, box-shadow 120ms ease-in',
                        '&: hover': {
                          backgroundColor: mainColorLighter,
                          color: mainColorDarker,
                          boxShadow: shadow2,
                        },
                      }}
                    >
                      <Box display="flex" gap={0.33} alignItems="center" sx={{ fontWeight: 600 }}>
                        {manualMode ? (
                          <AddIcon fontSize="small" />
                        ) : (
                          <DriveFileRenameOutlineIcon fontSize="small" />
                        )}
                        Edit Items
                      </Box>
                    </GlassOutlineButton>
                  ) : null}

                  {localManualCreate ? (
                    <Button
                      variant="text"
                      onClick={() => {
                        sounds?.click?.()
                        setLocalManualCreate(false)
                      }}
                      color="error"
                      disabled={isBusy}
                      sx={{
                        width: 'fit-content',
                        fontWeight: 700,
                        borderRadius: '22px',
                        py: 1,
                        px: 2,
                      }}
                    >
                      <Box display="flex" gap={0.33} alignItems="center" justifyContent="center">
                        <EditOffRoundedIcon fontSize="inherit" />
                        Stop Editing
                      </Box>
                    </Button>
                  ) : null}
                </Box>
              </Stack>
            </GlassCard>
          </Box>
          <Box className="editor-meta">
            <DiscoverabilityAccordion
              autoExpandKey={
                listCategories.length || listTags.length
                  ? [itemsAnimKey, ...listCategories, ...listTags].join('|')
                  : ''
              }
              onToggle={sounds?.click}
            >
              <Stack spacing={1}>
                <Stack spacing={1} pb={0.5}>
                  <Autocomplete
                    size="small"
                    value={listCategories}
                    onChange={(_e, value) => {
                      setListCategories(normalizeMetaList(value))
                    }}
                    multiple={true}
                    freeSolo={true}
                    disabled={isLocked || isBusy}
                    filterOptions={(options, params) => {
                      if (listCategories.length >= 3) return []
                      const filtered = filterOption(options, params)
                      const inputValue = params.inputValue?.trim().slice(0, 16)
                      if (
                        inputValue &&
                        !listCategories.some(
                          (value) => value.toLowerCase() === inputValue.toLowerCase()
                        )
                      ) {
                        filtered.push(inputValue)
                      }
                      return filtered
                    }}
                    options={mergedListCategoryOptions}
                    renderInput={({ InputProps, ...params }) => (
                      <TextField
                        InputProps={{
                          ...InputProps,
                          ...{
                            sx: {
                              backgroundColor: 'white',
                              borderRadius: '14px',
                              boxShadow: shadow1,
                              '& fieldset': {
                                borderColor: '#d6e4ef',
                              },
                            },
                          },
                        }}
                        {...params}
                        placeholder={listCategories.length >= 3 ? 'Limit reached' : 'Categories'}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option}>
                        {option}
                      </li>
                    )}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({
                          index,
                        })
                        return (
                          <Chip
                            variant="filled"
                            size="small"
                            label={normalizeMetaValue(option)}
                            key={key}
                            deleteIcon={
                              <ClearRoundedIcon
                                fontSize="inherit"
                                sx={{ fontSize: '13px !important' }}
                              />
                            }
                            sx={{
                              background: mainColor,
                              color: mainColorMediumText,
                              fontWeight: 600,
                              boxShadow: shadow1,
                            }}
                            {...tagProps}
                          />
                        )
                      })
                    }
                  />
                  <Autocomplete
                    size="small"
                    value={listTags}
                    onChange={(_e, value) => {
                      setListTags(normalizeMetaList(value))
                    }}
                    multiple={true}
                    freeSolo={true}
                    disabled={isLocked || isBusy}
                    filterOptions={(options, params) => {
                      if (listTags.length >= 3) return []
                      const filtered = filterOption(options, params)
                      const inputValue = params.inputValue?.trim().slice(0, 16)
                      if (
                        inputValue &&
                        !listTags.some((value) => value.toLowerCase() === inputValue.toLowerCase())
                      ) {
                        filtered.push(inputValue)
                      }
                      return filtered
                    }}
                    options={mergedListTagOptions}
                    renderInput={({ InputProps, ...params }) => (
                      <TextField
                        InputProps={{
                          ...InputProps,
                          ...{
                            sx: {
                              backgroundColor: 'white',
                              borderRadius: '14px',
                              boxShadow: shadow1,
                              '& fieldset': {
                                borderColor: '#d6e4ef',
                              },
                            },
                          },
                        }}
                        {...params}
                        placeholder={listTags.length >= 3 ? 'Limit reached' : 'Tags'}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option}>
                        {option}
                      </li>
                    )}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({
                          index,
                        })
                        return (
                          <Chip
                            variant="filled"
                            size="small"
                            label={normalizeMetaValue(option)}
                            key={key}
                            deleteIcon={
                              <ClearRoundedIcon
                                fontSize="inherit"
                                sx={{ fontSize: '13px !important' }}
                              />
                            }
                            sx={{
                              background: altColor,
                              color: altColorDarkText,
                              fontWeight: 600,
                              boxShadow: shadow1,
                            }}
                            {...tagProps}
                          />
                        )
                      })
                    }
                  />
                  <Stack spacing={0}>
                    <TextField
                      select
                      fullWidth
                      label="Visibility"
                      value={visibility}
                      onChange={(e) => setVisibility(e.target.value as 'public' | 'private')}
                      disabled={isLocked || isBusy || forcedPrivateByModeration}
                      InputProps={{
                        sx: {
                          background: '#ffffff',
                          borderRadius: '14px',
                          boxShadow: shadow1,
                          '& fieldset': {
                            borderColor: '#d6e4ef',
                          },
                        },
                      }}
                      size="small"
                    >
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                    </TextField>
                    <Typography variant="caption" color={helperTextColor} textAlign="left" pl={1}>
                      {forcedPrivateByModeration
                        ? "This list can't be set to public."
                        : visibility === 'public'
                          ? 'Visible and playable by the community.'
                          : 'Private and only shareable by link.'}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </DiscoverabilityAccordion>
          </Box>
          <Box className="editor-notice">
            {suggestPoll && !pollSuggestionDismissed ? (
              <Grid item xs={12}>
                <GlassCard
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.5, sm: 2 },
                    mb: 2,
                    background: '#ffffff',
                    border: `1px solid ${altColor}`,
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color={altColorDarkText}
                      textAlign="left"
                    >
                      Create a Poll instead?
                    </Typography>
                    <Typography variant="body1" textAlign="left" fontWeight={500}>
                      Trivia should be objective lists.
                    </Typography>
                    <Typography variant="body2" color={helperTextColor} textAlign="left">
                      Opinion-based topics work better as polls, where community votes determine the
                      ranking.
                    </Typography>
                    <Box display="flex" justifyContent="space-between" gap={1} pt={0.5}>
                      <Button
                        variant="text"
                        onClick={() => {
                          sounds?.click?.()
                          setPollSuggestionDismissed(true)
                        }}
                        color="error"
                        sx={{ borderRadius: '12px', px: 2 }}
                      >
                        Ignore
                      </Button>
                      <GlassOutlineButton
                        variant="contained"
                        onClick={() => {
                          sounds?.click?.()
                          onConvertToPoll?.({
                            prompt: topic,
                            items: listItems
                              .slice()
                              .sort((a, b) => a.index - b.index)
                              .map((item) => item.title?.trim())
                              .filter(Boolean)
                              .slice(0, 10) as string[],
                            categories: listCategories.slice(0, 3),
                            tags: listTags.slice(0, 3),
                            visibility,
                          })
                        }}
                        sx={{
                          color: altColorDarkText,
                          borderColor: altColor,
                          background: altColorLighter,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          Convert to Poll
                          <HowToVoteRoundedIcon fontSize="small" />
                        </Box>
                      </GlassOutlineButton>
                    </Box>
                  </Stack>
                </GlassCard>
              </Grid>
            ) : null}
          </Box>
        </EditorGrid>
      </Box>

      {isLocked ? (
        <Box
          display="flex"
          gap={1}
          justifyContent="space-between"
          alignItems="center"
          px={{
            xs: 1,
            smn: 0,
          }}
        >
          <Button
            variant="outlined"
            // On click should open edit list of this list
            onClick={handleViewList}
            disabled={!selectedList || isBusy}
            sx={{
              backgroundColor: mainColorLighter,
              borderColor: mainColorLighter,
              color: mainColorDarker,
              fontWeight: 500,
              borderRadius: '32px',
              px: 2,
              fontSize: '14px',
              '&: hover': {
                borderColor: mainColorLighter,
              },
            }}
          >
            <Box display="flex" gap={0.5} alignItems="center">
              View List
              <OpenInNewRoundedIcon fontSize="small" sx={{ fontSize: '17px' }} />
            </Box>
          </Button>

          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{
              backgroundColor: mainColorLighter,
              borderColor: mainColorLighter,
              color: mainColorDarker,
              fontWeight: 500,
              borderRadius: '32px',
              px: 2,
              fontSize: '14px',
              '&: hover': {
                borderColor: mainColorLighter,
              },
            }}
          >
            <Box display="flex" gap={0.5} alignItems="center">
              <ReplayRoundedIcon fontSize="small" sx={{ fontSize: '17px' }} />
              New List
            </Box>
          </Button>
        </Box>
      ) : null}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
        gap={2}
        px={{
          xs: 1,
          sm: 0,
          md: 0,
          lg: 0,
        }}
        flexDirection={{
          xs: 'column',
          sm: 'row',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          width={{
            xs: '100%',
            sm: 'inherit',
            md: 'inherit',
            lg: 'inherit',
          }}
        >
          {topic &&
            listItems.length === 10 &&
            (!userId && !selectedList ? (
              <Chip
                sx={{
                  width: {
                    xs: '100%',
                    sm: undefined,
                  },
                }}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <InfoOutlineRoundedIcon fontSize="small" color="action" />

                    <Typography
                      variant="body2"
                      sx={{
                        color: helperTextColor,
                        fontWeight: 500,
                      }}
                    >
                      Log in to save lists
                    </Typography>
                  </Box>
                }
              />
            ) : (
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                width={{
                  xs: '100%',
                  sm: 'inherit',
                  md: 'inherit',
                  lg: 'inherit',
                }}
              >
                <GlassOutlineButton
                  disabled={
                    !userId ||
                    !!selectedList ||
                    !!saving ||
                    isBusy ||
                    !hasValidItemCount ||
                    !hasAllTitles
                  }
                  onClick={handleCreateList}
                  variant={selectedList ? 'contained' : 'outlined'}
                  startIcon={
                    selectedList ? (
                      <CheckRoundedIcon fontSize="small" />
                    ) : (
                      <BookmarkAddIcon fontSize="small" />
                    )
                  }
                  fullWidth
                  sx={{
                    color: mainColorDarker,
                    backgroundColor: '#FFF',
                    // borderColor: selectedList ? mainColorDarker : 'white',
                    borderWidth: '1.5px',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 5,
                    height: '56px',
                    boxShadow: shadow2,
                    minWidth: '250px',
                    transition:
                      'transform 120ms ease-out, box-shadow 120ms ease-out, background-color 150ms ease-out, color 150ms ease-out',
                    '@media (hover: hover) and (pointer: fine)': {
                      '&:hover': {
                        borderColor: mainColor,
                        outlineColor: mainColor,
                        borderWidth: '1.5px',
                        transform: 'translateY(-1px) scale(1.02)',
                        boxShadow: shadow2,
                        backgroundColor: selectedList ? mainColorDarker : `${mainColorDarker}10`,
                      },
                    },
                    '&:active': {
                      borderColor: mainColor,
                      outlineColor: mainColor,
                      transform: 'scale(0.98)',
                      boxShadow: shadow2,
                    },
                  }}
                >
                  <Typography fontSize={18} fontWeight={700}>
                    {saving ? 'Saving...' : selectedList ? 'List Saved' : 'Save to My Trivia'}
                  </Typography>
                </GlassOutlineButton>
              </Box>
            ))}
        </Box>

        <PlayButton
          sx={{
            minWidth: '250px',
            fontSize: 22,
          }}
          disabled={!topic || isBusy || !listItems?.length || !hasValidItemCount || !hasAllTitles}
          onClick={handleStartGame}
        />
      </Box>
      {!hasValidItemCount || !hasAllTitles ? (
        <Box display="flex" justifyContent="flex-end" alignItems="center" px={1} mt={1}>
          <Typography variant="body2" color={helperTextColor}>
            Must have 10 items
          </Typography>
        </Box>
      ) : null}

      <Dialog
        open={manualModeModalOpen}
        aria-labelledby="edit-items-dialog-title"
        aria-describedby="edit-items-dialog-description"
        onClose={() => setManualModeModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            m: 2,
            width: 'calc(100% - 32px)',
            borderRadius: '28px',
            background: '#ffffff',
            border: '1px solid #bddfeb',
            boxShadow: '0 5px 0 #9fc9dc, 0 20px 60px rgba(25,59,88,0.18)',
          },
        }}
      >
        <DialogTitle id="edit-items-dialog-title" sx={{ p: 3, pb: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                borderRadius: '14px',
                background: '#e1f5fb',
                color: '#193b58',
                boxShadow: '0 3px 0 #bddfeb',
              }}
            >
              <DriveFileRenameOutlineIcon />
            </Box>
            <Typography component="span" variant="h3" sx={{ fontSize: 26, color: '#193b58' }}>
              Edit Items?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 0 }}>
          <Typography
            id="edit-items-dialog-description"
            sx={{
              color: '#516f86',
              fontSize: 15,
              lineHeight: 1.6,
              textAlign: 'left',
            }}
          >
            Customize your trivia to share with friends or host a game.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              alignItems: 'flex-start',
              mt: 2,
              p: 1.75,
              borderRadius: '16px',
              background: '#eff9fd',
              border: '1px solid #cde7f1',
            }}
          >
            <InfoOutlineRoundedIcon sx={{ color: '#087fa2', fontSize: 20, mt: 0.25 }} />
            <Box>
              <Typography
                sx={{
                  color: '#193b58',
                  fontWeight: 700,
                  fontSize: 14,
                  textAlign: 'left',
                }}
              >
                The answers will be revealed
              </Typography>
              <Typography
                sx={{
                  color: '#516f86',
                  fontSize: 13,
                  lineHeight: 1.5,
                  textAlign: 'left',
                  mt: 0.5,
                }}
              >
                Editing shows each item&apos;s details and correct rank, so save the challenge for
                someone else.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1.5 }}>
          <GlassOutlineButton
            variant="outlined"
            onClick={() => {
              sounds?.click?.()
              setManualModeModalOpen(false)
            }}
            sx={{ flex: 1, color: '#193b58', fontWeight: 700 }}
          >
            Cancel
          </GlassOutlineButton>
          <GlassButton
            variant="contained"
            startIcon={<CheckCircleRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              sounds?.click?.()
              setListItems((prev) => normalizeItems(prev))
              setLocalManualCreate(true)
              setManualModeModalOpen(false)
              if (!listItems?.length) {
                handleAddItem()
              }
            }}
            sx={{ flex: 1, fontWeight: 800 }}
          >
            Confirm
          </GlassButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
